import React, { useState, ReactNode } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useSiteConfig } from '../context/SiteCustomizationContext';

const PublicLayout: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const { allConfig } = useSiteConfig();
  const [open, setOpen] = useState(false);

  const nav = [
    { text: 'Accueil', link: '/' },
    { text: 'Services', link: '/services' },
    { text: 'Simulateur', link: '/simulateur' },
    { text: 'À propos', link: '/a-propos' },
    { text: 'Blog', link: '/blog' },
    { text: 'Contact', link: '/contact' },
  ];

  const cta = {
    primary: { text: allConfig?.home?.hero?.ctaText || 'Demander un devis', link: allConfig?.home?.hero?.ctaLink || '/devis' },
    secondary: { text: 'Espace Client', link: '/login' },
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="sticky top-0 z-50 border-b bg-white">
        <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src={allConfig?.identity?.logoUrl || '/logo.png'}
              alt={allConfig?.identity?.siteName || 'Elecborn'}
              className="h-10 w-auto"
            />
            <span className="font-semibold text-lg">
              {allConfig?.identity?.siteName || 'Elecborn'}
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            {nav.map((item) => (
              <NavLink
                key={item.link}
                to={item.link}
                className={({ isActive }) =>
                  `text-sm ${isActive ? 'text-blue-600 font-medium' : 'text-gray-700 hover:text-gray-900'}`
                }
              >
                {item.text}
              </NavLink>
            ))}
            <Link
              to={cta.primary.link}
              className="rounded-xl px-4 py-2 text-sm font-medium border border-blue-600 text-blue-600 hover:bg-blue-50"
            >
              {cta.primary.text}
            </Link>
          </nav>

          <button className="md:hidden" onClick={() => setOpen((v) => !v)}>
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t">
            <div className="px-4 py-2 flex flex-col gap-3">
              {nav.map((item) => (
                <NavLink
                  key={item.link}
                  to={item.link}
                  onClick={() => setOpen(false)}
                  className="py-2 text-gray-700"
                >
                  {item.text}
                </NavLink>
              ))}
              <Link
                to={cta.primary.link}
                onClick={() => setOpen(false)}
                className="rounded-xl px-4 py-2 text-sm font-medium border border-blue-600 text-blue-600 text-center"
              >
                {cta.primary.text}
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        {children}
        <Outlet />
      </main>

      <footer className="border-t bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 py-8 grid gap-6 md:grid-cols-3">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <img src={allConfig?.identity?.logoUrl || '/logo.png'} alt="logo" className="h-8 w-auto" />
              <span className="font-semibold">
                {allConfig?.identity?.siteName || 'Elecborn'}
              </span>
            </div>
            <p className="text-sm text-gray-600">
              {allConfig?.identity?.slogan ||
                'Votre partenaire en solutions de recharge électrique.'}
            </p>
          </div>
          <div>
            <div className="font-semibold mb-3">Navigation</div>
            <div className="grid gap-2">
              {nav.map((item) => (
                <NavLink key={item.link} to={item.link} className="text-sm text-gray-700 hover:underline">
                  {item.text}
                </NavLink>
              ))}
            </div>
          </div>
          <div>
            <div className="font-semibold mb-3">Liens</div>
            <div className="grid gap-2">
              <Link to="/mentions-legales" className="text-sm text-gray-700 hover:underline">
                Mentions légales
              </Link>
              <Link to="/cgv" className="text-sm text-gray-700 hover:underline">
                CGV
              </Link>
              <Link to="/cookies" className="text-sm text-gray-700 hover:underline">
                Cookies
              </Link>
            </div>
          </div>
        </div>
        <div className="text-center text-xs text-gray-500 pb-6">
          © {new Date().getFullYear()} {allConfig?.identity?.siteName || 'Elecborn'}. Tous droits réservés.
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
